import { AlertDefaults, Brand, SettingsApi } from 'src/app/hickory-api';
import { StringValues } from 'src/utility/string-values';
import { AlertMessageHelper } from './alert-message-helper';
import { AlertModelService } from '../alert-model.service';
import { AlertFields } from './alert-fields-helper';

export class AlertBrandsHelper {

  static postProcess(service: AlertModelService, alert: AlertDefaults = service.alertSettings, settingsApi: SettingsApi = service.settingsApi) {
    if (service.useBrandingSendFeature) {
      // assume that no tenant will have more than 200 brands
      settingsApi?.getBrands(1, 200).subscribe(response => {
        response?.items?.sort((a, b) => {
          if (a.isDefault === true) return -1;
          else if (b.isDefault === true) return 1;
          return a.brandName?.toLowerCase().localeCompare(b.brandName?.toLowerCase())
        });
        service.state.brands = response.items;

        const defaultBrand = this.determineDefaultBrand(service, alert?.brandId)
        if (service.isEditingTemplate && !!alert?.brandId) {
          // if editingTemplate and valid brandId
          // apply the selected brand so that it's visible on the form
          this.applyBrand(service, defaultBrand, false);
        } else if (!service.isEditingTemplate && !alert.brandId) {
          // if NOT editingTemplate and NOT valid brandId
          // just set the default brandId
          service.alertSettings.brandId = alert.brandId = defaultBrand?.id;
        }

        service.notifyFieldUpdated(AlertFields.BRAND);
      });
    }
  }

  static preparePreview(service: AlertModelService, preview: AlertDefaults) {
    // when saving a template, null out any values that came from the brand
    // they will be re-applied at send-time (and kept up-to-date with the brand)
    if (service.isEditingTemplate) {
      this.clearValuesFromBrand(service, preview);
    }
  }

  static clearValuesFromBrand(service: AlertModelService, alert: AlertDefaults = service.alertSettings) {
    const brand = this.determineDefaultBrand(service, alert.brandId);
    if (brand) {
      alert.message = AlertMessageHelper.initMessage(alert.message);
      alert.sender = alert.sender ?? {};
      const sender = alert.sender;
      const basic = alert.message.basic;

      if (!!sender.displayName?.length && sender.displayName === brand.defaultDisplayName) { sender.displayName = undefined; }
      if (!!sender.email?.length && sender.email === brand.defaultReplyTo) { sender.email = undefined; }
      if (!!sender.callerId?.length && sender.callerId === brand.defaultCallerId) { sender.callerId = undefined; }
      if (!!basic.subject?.length && basic.subject === brand.defaultSubject) { basic.subject = undefined; }
    }
  }

  static applyBrand(service: AlertModelService, brand: Brand, preferBrandValues: boolean = true) {
    const alert = service.alertSettings;
    alert.brandId = brand?.id;
    alert.message = AlertMessageHelper.initMessage(alert.message);
    alert.sender = alert.sender ?? {};
    const sender = alert.sender;
    const basic = alert.message.basic;

    // this (currently) affects values in the SenderSettings and the BasicMessage.subject

    // if preferBrandValues === true, then non-empty values from the brand will overwrite values on the alert
    // else values from the alert will take precendece.
    if (preferBrandValues) {
      sender.displayName = StringValues.replaceIfEmpty(brand?.defaultDisplayName, sender?.displayName);
      sender.email = StringValues.replaceIfEmpty(brand?.defaultReplyTo, sender?.email);
      sender.callerId = StringValues.replaceIfEmpty(brand?.defaultCallerId, sender?.callerId);
      alert.message.basic.subject = StringValues.replaceIfEmpty(brand?.defaultSubject, alert.message.basic.subject);
    } else {
      sender.displayName = StringValues.replaceIfEmpty(sender?.displayName, brand?.defaultDisplayName);
      sender.email = StringValues.replaceIfEmpty(sender?.email, brand?.defaultReplyTo);
      sender.callerId = StringValues.replaceIfEmpty(sender?.callerId, brand?.defaultCallerId);
      basic.subject = StringValues.replaceIfEmpty(basic.subject, brand?.defaultSubject);
    }

    service.notifyFieldUpdated(AlertFields.BRAND);
  }

  static applyDefaultBrand(service: AlertModelService): Brand {
    const brand = this.determineDefaultBrand(service);

    // if we're working with a template the user selected (template has an ID), then we don't
    // want the auto-selection of the default brand to overwrite non-empty values from the template.
    const preferBrandValues = !service.alertTemplate?.id;
    this.applyBrand(service, brand, preferBrandValues);
     // make sure the brandId is set to the value from defaults (may be null)
    service.alertSettings.brandId =  service.isEditingTemplate ? service.alertDefaults?.brandId : brand?.id;
    service.notifyFieldUpdated(AlertFields.BRAND);
    return brand;
  }

  static determineDefaultBrand(service: AlertModelService, brandId: number = service.alertDefaults?.brandId): Brand {
    // if we have a brandId specified on our alert settings, apply that brand
    // else look for isDefault==true and apply that brand (if present)
    return service?.tenantBrands?.find(x => x.id === brandId) ?? service?.tenantBrands?.find(x => x.isDefault === true);
  }

  static isDefaultBrand(service: AlertModelService): boolean {
    return service.alertDefaults?.brandId === service.alertSettings?.brandId;
  }
}
